/**
 * NegotiationCenter — Cockpit "Centre de négociations" (orchestrateur).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Download, RefreshCw, LayoutGrid,
} from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { useNegotiationSocket } from '../../hooks/useNegotiationSocket';
import { NegoKpiCards } from './NegoKpiCards';
import { SlaBanner } from './SlaBanner';
import { NegoToolbar } from './NegoToolbar';
import { NegoTable } from './NegoTable';
import { NegoDrawer } from './NegoDrawer';
import { NewNegotiationDialog } from './NewNegotiationDialog';
import { NegoKanban } from './NegoKanban';
import {
  STATUS_META, slaInfo, initialOffer, negotiatedOffer, offerGap, fmtRelative, fmtXof,
} from './negoMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH = { headers: { Authorization: 'Bearer cookie-auth' } };

const TAB_PARAMS = {
  active: { status_filter: 'active' },
  overdue: { is_overdue: 'true' },
  escalated: { status_filter: 'escalated' },
  pending_client: { status_filter: 'pending_client' },
  pending_internal: { status_filter: 'pending_internal' },
  mine: { assigned_to_me: 'true' },
  all: {},
};

const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, m]) => ({ value, label: m.label }));

export const NegotiationCenter = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [types, setTypes] = useState([]);
  const [assignees, setAssignees] = useState([]);

  const [activeTab, setActiveTab] = useState('active');
  const [search, setSearch] = useState('');
  const [typeFilters, setTypeFilters] = useState([]);
  const [statusFilters, setStatusFilters] = useState([]);
  const [sortBy, setSortBy] = useState('lastActivity');
  const [view, setView] = useState('table');
  const [bannerClosed, setBannerClosed] = useState(false);

  const [selected, setSelected] = useState(new Set());
  const [drawerId, setDrawerId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const params = new URLSearchParams(view === 'kanban' ? {} : (TAB_PARAMS[activeTab] || {}));
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/negotiations?${params}`, AUTH),
        axios.get(`${API}/negotiations/stats/overview`, AUTH),
      ]);
      setRows(listRes.data.negotiations || []);
      setStats(statsRes.data);
      setLastSync(new Date().toISOString());
    } catch (e) {
      // garder les données précédentes
    } finally { setLoading(false); setRefreshing(false); }
  }, [activeTab, view]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/negotiations/types`, AUTH); setTypes(data.types || []); } catch {}
      try { const { data } = await axios.get(`${API}/admin/commercials`, AUTH); setAssignees(data.commercials || []); } catch {}
    })();
  }, []);

  const { isConnected } = useNegotiationSocket({ onEvent: () => load(), showToasts: false });

  // Filtrage client (recherche + multi type/statut)
  const filteredRows = useMemo(() => {
    let list = [...rows];
    if (activeTab === 'mine' && user?.id) {
      list = list.filter((n) => [n.assignedSalesId, n.decisionOwnerId, n.assignedOpsId, n.escalatedTo, n.createdBy].includes(user.id));
    }
    if (typeFilters.length) list = list.filter((n) => typeFilters.includes(n.negotiationType));
    if (statusFilters.length) list = list.filter((n) => statusFilters.includes(n.status));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((n) =>
        n.operationNumber?.toLowerCase().includes(q)
        || n.clientName?.toLowerCase().includes(q)
        || n.assignedSalesName?.toLowerCase().includes(q)
        || n.createdByName?.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      switch (sortBy) {
        case 'sla': return new Date(a.slaDeadlineAt || 0) - new Date(b.slaDeadlineAt || 0);
        case 'amountDesc': return (negotiatedOffer(b) || 0) - (negotiatedOffer(a) || 0);
        case 'gapAsc': return (offerGap(a) ?? 0) - (offerGap(b) ?? 0);
        default: return new Date(b.lastActivityAt || 0) - new Date(a.lastActivityAt || 0);
      }
    });
    return list;
  }, [rows, activeTab, user, typeFilters, statusFilters, search, sortBy]);

  const toggleSelect = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelected((p) => p.size === filteredRows.length ? new Set() : new Set(filteredRows.map((r) => r.id)));

  const exportCsv = () => {
    const items = selected.size ? filteredRows.filter((r) => selected.has(r.id)) : filteredRows;
    if (!items.length) { toast.info('Aucune donnée à exporter'); return; }
    const head = ['Operation', 'Client', 'Trajet', 'Offre_initiale', 'Offre_negociee', 'Ecart', 'Responsable', 'Statut', 'SLA'];
    const csv = ['\ufeff' + head.join(';')].concat(items.map((n) => [
      n.operationNumber || '', n.clientName || '',
      `${n.departureCity || ''} -> ${n.arrivalCity || ''}`,
      initialOffer(n) ?? '', negotiatedOffer(n) ?? '', offerGap(n) ?? '',
      n.assignedSalesName || n.createdByName || '', STATUS_META[n.status]?.label || n.status,
      slaInfo(n).label,
    ].map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a'); a.href = url; a.download = `negociations_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`${items.length} négociation(s) exportée(s)`);
  };

  const quickAction = async (nego, action) => {
    if (action === 'reject' && !window.confirm('Confirmer le refus de cette négociation ?')) return;
    try {
      await axios.post(`${API}/negotiations/${nego.id}/actions`, { action }, AUTH);
      const labels = { accept: 'Négociation acceptée avec succès', reject: 'Négociation refusée', escalate: 'Dossier escaladé au Lead' };
      toast.success(labels[action] || 'Action enregistrée');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'action"); }
  };

  const riskCount = stats?.overdue || 0;
  const drawerSeed = filteredRows.find((r) => r.id === drawerId) || null;

  return (
    <div className={`space-y-5 transition-all ${drawerId ? 'xl:pr-[470px]' : ''}`} data-testid="negotiation-center">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Centre de négociations</h1>
            <span data-testid="nego-online-badge" className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${isConnected ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-slate-400'}`} />
              {isConnected ? 'En ligne' : 'Hors ligne'}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-500">Pilotez les offres en discussion, les décisions commerciales et les dossiers à risque SLA.</p>
          {!isConnected && <p className="mt-0.5 text-xs text-slate-400">Les décisions seront synchronisées au retour de la connexion.</p>}
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden flex-col items-end pr-1 text-right sm:flex">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">Dernière synchro</span>
            <span className="text-xs font-medium text-slate-600" data-testid="nego-last-sync">{lastSync ? fmtRelative(lastSync).toLowerCase() : '—'}</span>
          </div>
          <Button className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => setNewOpen(true)} data-testid="new-nego-btn"><Plus className="mr-1.5 h-4 w-4" /> Nouvelle négociation</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="nego-export-csv"><Download className="mr-1.5 h-4 w-4" /> Exporter CSV</Button>
          <Button variant="outline" onClick={() => load(true)} disabled={refreshing} data-testid="nego-refresh"><RefreshCw className={`mr-1.5 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Actualiser</Button>
        </div>
      </div>

      {!bannerClosed && <SlaBanner riskCount={riskCount} onSeeUrgent={() => { setActiveTab('overdue'); }} onClose={() => setBannerClosed(true)} />}

      <NegoKpiCards stats={stats} />

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <NegoToolbar
          activeTab={activeTab} onTab={(t) => { setActiveTab(t); setSelected(new Set()); }} stats={stats}
          search={search} onSearch={setSearch}
          types={types} typeFilters={typeFilters} onTypeFilters={setTypeFilters}
          statusFilters={statusFilters} onStatusFilters={setStatusFilters} statusOptions={STATUS_OPTIONS}
          sortBy={sortBy} onSort={setSortBy} view={view} onView={setView}
        />
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between rounded-xl bg-slate-900 px-4 py-2.5 text-white" data-testid="nego-bulk-bar">
          <span className="text-sm font-medium tabular-nums">{selected.size} sélectionnée{selected.size > 1 ? 's' : ''}</span>
          <Button size="sm" variant="ghost" className="h-7 text-white hover:bg-white/10" onClick={exportCsv}><Download className="mr-1 h-3.5 w-3.5" /> Exporter</Button>
        </div>
      )}

      {view === 'table' ? (
        <NegoTable
          rows={filteredRows} loading={loading} selected={selected}
          onSelect={toggleSelect} onSelectAll={toggleSelectAll}
          onOpen={setDrawerId} onAction={quickAction}
          emptyTab={['overdue', 'escalated'].includes(activeTab)}
          onSeeAll={() => setActiveTab('all')} onCreate={() => setNewOpen(true)}
        />
      ) : (
        <NegoKanban rows={filteredRows} loading={loading} onOpen={setDrawerId} onAction={quickAction} />
      )}

      <NegoDrawer
        negotiationId={drawerId} seed={drawerSeed} open={!!drawerId}
        onClose={() => setDrawerId(null)} onActionDone={load} assignees={assignees}
      />
      <NewNegotiationDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={load} types={types} />
    </div>
  );
};

export default NegotiationCenter;
